import type { SalesOrderItem } from '@/lib/api/sales-order-api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { m } from '@/lib/paraglide/messages'

interface Props {
    item: SalesOrderItem
    /** Người dùng có quyền nối (products.update). */
    canLink: boolean
    /** Đơn còn nối/nối lại được (chưa xuất kho). */
    linkable: boolean
    onLink: (item: SalesOrderItem) => void
}

/** Dòng "Mã KiotViet: X · ĐVT: Y" — dùng chung cho ô dòng đơn và popup nối.
 *  Dòng hoá đơn không có mã (dịch vụ / hàng đã xoá bên KiotViet) ghi rõ. */
export function kiotVietLineMeta(item: SalesOrderItem): string {
    const code = item.kiotviet_product_code
    if (!code) return m.kiotviet_line_meta_no_code()
    return item.kiotviet_unit
        ? m.kiotviet_line_meta_with_unit({ code, unit: item.kiotviet_unit })
        : m.kiotviet_line_meta({ code })
}

/** Tên hiển thị của dòng KiotViet (tên KiotViet → tên dòng → nhãn mặc định). */
export function kiotVietLineName(item: SalesOrderItem): string {
    return (
        item.kiotviet_product_name ||
        item.product_name ||
        m.kiotviet_line_unnamed()
    )
}

/**
 * Ô tên SP của một dòng đơn (M-145). Dòng KiotViet chưa nối (kể cả dòng không
 * mã): tên + mã + ĐVT KiotViet, nhãn "Chưa nối", nút "Nối sản phẩm" (khi có
 * quyền và đơn chưa xuất kho). Dòng khác (kể cả dòng KiotViet đã nối): chỉ tên.
 */
export function KiotVietItemCell({ item, canLink, linkable, onLink }: Props) {
    if (!item.kiotviet_unlinked) return <>{item.product_name}</>
    return (
        <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
                <span>{kiotVietLineName(item)}</span>
                <Badge variant="destructive">
                    {m.kiotviet_item_unlinked_badge()}
                </Badge>
            </div>
            <span className="text-xs text-muted-foreground">
                {kiotVietLineMeta(item)}
            </span>
            {canLink && linkable ? (
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="w-fit"
                    onClick={() => onLink(item)}
                >
                    {m.kiotviet_item_link_button()}
                </Button>
            ) : null}
        </div>
    )
}
